import type { ApolloError } from '@apollo/client';
import type { QueryProps } from 'lib/api';
import type { Page, Settings, Review, Pagination, Translation } from 'types';
import type { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import type { PartnersReviewsQuery } from 'graphql/research/types-and-hooks';
import camelcaseKeys from 'camelcase-keys';
import { getPageData } from 'lib/queries/pages';
import { getSettings } from 'lib/queries/settings';
import { refetchPartnersReviewsQuery as fetchPartnersReviewsQuery } from 'graphql/research/types-and-hooks';
import { queryResearch } from 'lib/apollo';
import { request } from 'lib/rest';

export type ReviewsListResult = {
  reviews: Review[];
  pagination: Pagination;
};

export async function getReviews(page = 1): Promise<ReviewsListResult> {
  const perPage = 12;
  const { error, data } = await request<Review[]>(
    `/review?_fields=id,content,acf,featured_image,featured_media,type&page=${page}&per_page=${perPage}`,
  );

  if (error) {
    throw error;
  }

  return {
    reviews: camelcaseKeys(data.items, { deep: true }),
    pagination: {
      total: data.totalItems,
      limit: perPage,
      offset: (page - 1) * perPage,
    },
  };
}

export type ReviewsPageProps = {
  page: Page;
  settings: Settings;
  reviewsList: ReviewsListResult;
  isPreview: boolean;
};

export async function getReviewsPage({
  query,
  locale,
  preview,
}: GetServerSidePropsContext): Promise<
  GetServerSidePropsResult<ReviewsPageProps>
> {
  const page = query.page ? Number(query.page) : 1;

  if (page === 0) {
    return {
      notFound: true,
    };
  }

  try {
    const [pageData, reviewsList, settings] = await Promise.all([
      getPageData({ locale, slug: '/reviews', preview }),
      getReviews(page),
      getSettings(locale),
    ]);

    if (!pageData || !settings || !reviewsList) {
      return {
        notFound: true,
      };
    }

    //generate languageAlternates - hreflang - array
    const hrefLangAnnotations = () => {
      if (pageData?.translations?.length > 0) {
        const annotations: Omit<Translation, 'locale'>[] =
          pageData?.translations;

        if (
          locale === 'en' ||
          pageData?.translations?.find(({ slug }) => slug === 'en')
        ) {
          //hreflang must have x-default
          annotations.push({
            uri:
              locale === 'en'
                ? '/reviews'
                : pageData?.translations?.find(({ slug }) => slug === 'en')
                    ?.uri,
            slug: 'x-default',
          });
        }
        return annotations;
      }
      return null;
    };

    const languageAlternates = hrefLangAnnotations()
      ? hrefLangAnnotations()?.map(({ slug, uri }) => ({
          hrefLang: slug,
          href: uri ? `https://leafwell.com${uri}` : null,
        }))
      : null;

    return {
      props: {
        page: pageData,
        reviewsList,
        settings: {
          ...settings,
          pageTranslations: pageData.translations,
          languageAlternates,
        },
        isPreview: !!preview,
      },
    };
  } catch (error) {
    console.log(`error getting reviews page:`, JSON.stringify(error));

    if (error.status === 400) {
      return {
        notFound: true,
      };
    }

    throw error;
  }
}

export type PartnersReviewsResult = {
  partnersReviews?: Review[];
  error?: ApolloError;
};

export async function getPartnersReviews({
  client,
}: Pick<QueryProps, 'client'>): Promise<PartnersReviewsResult> {
  const { error: partnersReviewsError, ...partnersReviews } =
    await queryResearch<PartnersReviewsQuery>(
      client,
      fetchPartnersReviewsQuery(),
    );
  let filteredReviews = [];

  if (partnersReviews?.data?.nodes.length) {
    /* merge every partners reviews and select the most recent 8 */
    const reviews = partnersReviews.data?.nodes
      .reduce(function (filteredArr, { reviews }) {
        const parsedReviews = JSON.parse(reviews);
        filteredArr.push(...parsedReviews);
        return filteredArr;
      }, [])
      .filter(({ rating }) => rating >= 4)
      .filter(({ text }) => text?.length > 0)
      .sort((a, b) => b.time - a.time)
      .slice(0, 8);

    if (reviews) {
      filteredReviews = reviews.map(
        (
          { author_name, profile_photo_url, rating: reviewRating, text, time },
          index,
        ) => ({
          id: index.toString(),
          date: time * 1000, //convert to miliseconds for new Date()
          featuredMedia: {
            id: index.toString(),
            src: profile_photo_url,
          },
          content: text,
          acf: {
            rating: reviewRating,
            reviewAuthor: { name: author_name },
          },
        }),
      );
    }
  }

  return {
    error: partnersReviewsError,
    partnersReviews: filteredReviews,
  };
}
